import React, { useState, useRef } from 'react';
import { useBooking, Service } from '../../contexts/booking/BookingContext';
import { useAuth } from '../../contexts/auth/AuthContext';
import { supabase, SUPABASE_ANON_KEY } from '../../lib/supabaseClient';
import { format, parseISO, addMinutes } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import {
  getNumericPrice,
  getNumericDuration,
  getTotalDuration,
  getTotalPrice,
  formatPrice,
  calculatePricingBreakdown
} from '../../utils/bookingUtils';
import { edgeFunctionUrl, EdgeFunctions } from '../../lib/edgeFunctions';

/**
 * Component for reviewing and confirming booking details
 */
const BookingSummary: React.FC = () => {
  const { bookingState, prevStep, nextStep, setAppointmentId } = useBooking();
  const { isMember, userProfile } = useAuth();
  const { selectedServices, selectedStaff, selectedDate, selectedTime, customerInfo } = bookingState;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailSentRef = useRef(false); // Track if email was already sent
  const submissionInProgressRef = useRef(false); // Prevent multiple simultaneous submissions

  // Calculate pricing with member discount
  const walletBalance = userProfile?.wallet_balance || 0;
  const pricing = calculatePricingBreakdown(selectedServices, isMember, walletBalance);
  
  // Validate required data
  if (!selectedDate || !selectedTime || !customerInfo) {
    return <div>Missing required booking information. Please go back and complete all steps.</div>;
  }
  
  if (!selectedTime) {
    return <div>Error: Booking time not selected. Please go back.</div>;
  }
  
  // Parse the stored UTC time and convert to Texas timezone for display
  const utcDateTime = parseISO(selectedTime);
  const appointmentDateTime = toZonedTime(utcDateTime, 'America/Chicago');
  const totalDuration = getTotalDuration(selectedServices);
  const appointmentEndTime = addMinutes(appointmentDateTime, totalDuration);
  
  // For saving to database, strip 'Z' to match DB format (timestamp without time zone)
  // This matches how admin saves appointments
  const startTimeISO = selectedTime.replace('Z', '');
  const endTimeISO = addMinutes(utcDateTime, totalDuration).toISOString().replace('Z', '');
  
  /**
   * Handles the booking submission to the database
   */
  const handleSubmitBooking = async () => {
    // Prevent duplicate submissions
    if (submissionInProgressRef.current) {
      return;
    }
    
    try {
      submissionInProgressRef.current = true;
      setIsSubmitting(true);
      setError(null);
      
      // Get current authenticated user (if any)
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get or create client ID
      const clientId = await getOrCreateClientId(user);
      
      // Create the appointment with pending_payment status
      const appointmentId = await createAppointment(clientId, startTimeISO, endTimeISO);
      
      // Add services to the appointment
      await addServicesToAppointment(appointmentId);

      // Store the appointment ID in the booking context
      setAppointmentId(appointmentId);

      // Send booking confirmation email ONLY ONCE
      if (!emailSentRef.current && customerInfo.email) {
        emailSentRef.current = true;
        sendBookingConfirmationEmail(appointmentId, customerInfo.email).catch(err => {
          console.error('Error sending confirmation email:', err);
        });
      }

      // Move to the payment step
      nextStep();
      
    } catch (err: any) {
      console.error('Error creating booking:', err);
      setError(err.message || 'An error occurred while creating your booking.');
    } finally {
      setIsSubmitting(false);
      submissionInProgressRef.current = false;
    }
  };
  
  /**
   * Gets existing client ID or creates a new client profile
   */
  const getOrCreateClientId = async (user: any): Promise<string> => {
    if (user) {
      const clientId = user.id;
      
      const { error: profileError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('id', clientId)
        .single();
        
      if (profileError) {
        const { error: insertError } = await supabase
          .from('user_profiles')
          .insert({
            id: clientId,
            full_name: `${customerInfo!.firstName} ${customerInfo!.lastName}`,
            preferred_name: customerInfo!.firstName,
            email: customerInfo!.email,
            phone_number: customerInfo!.phone,
            account_status: 'active'
          });
          
        if (insertError) {
          throw new Error(`Error creating profile: ${insertError.message}`);
        }
      }
      
      return clientId;
    }
    
    // User not authenticated - use RPC function to get or create guest profile
    const { data: profileId, error: rpcError } = await supabase
      .rpc('get_or_create_guest_profile', {
        p_email: customerInfo!.email,
        p_full_name: `${customerInfo!.firstName} ${customerInfo!.lastName}`,
        p_preferred_name: customerInfo!.firstName,
        p_phone: customerInfo!.phone
      });

    if (rpcError) {
      throw new Error(`Error creating guest profile: ${rpcError.message}`);
    }

    if (!profileId) {
      throw new Error('Failed to get or create guest profile');
    }

    return profileId;
  };
  
  /**
   * Creates the main appointment record
   */
  const createAppointment = async (clientId: string, startTime: string, endTime: string): Promise<string> => {
    const appointmentId = crypto.randomUUID ? crypto.randomUUID() : `apt-${Date.now()}`;

    const { error: appointmentError } = await supabase
      .from('appointments')
      .insert({
        id: appointmentId,
        client_id: clientId,
        service_id: selectedServices[0].id,
        staff_id: selectedStaff?.id || null,
        start_time: startTime,
        end_time: endTime,
        status: 'scheduled',
        payment_status: 'pending',
        source: 'online',
        notes: customerInfo?.notes || null,
      });

    if (appointmentError) {
      throw new Error(`Error creating appointment: ${appointmentError.message}`);
    }

    return appointmentId;
  };
  
  /**
   * Adds all selected services to the appointment_services junction table
   */
  const addServicesToAppointment = async (appointmentId: string): Promise<void> => {
    const appointmentServices = selectedServices.map(service => ({
      appointment_id: appointmentId,
      service_id: service.id,
      price: getNumericPrice(service),
      duration: getNumericDuration(service),
      staff_id: selectedStaff?.id || null,
    }));

    const { error: servicesError } = await supabase
      .from('appointment_services')
      .insert(appointmentServices);

    if (servicesError) {
      throw new Error(`Error adding services to appointment: ${servicesError.message}`);
    }
  };

  /**
   * Sends booking confirmation email to customer
   */
  const sendBookingConfirmationEmail = async (appointmentId: string, email: string, submissionId?: string): Promise<void> => {
    try {
      const servicesText = selectedServices.map(s => s.name).join(', ');
      const totalPrice = getTotalPrice(selectedServices);

      // Get session token for authorization (works for both authenticated and guest users)
      const { data: { session } } = await supabase.auth.getSession();

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
      };

      // Add Authorization header if user is logged in
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(edgeFunctionUrl(EdgeFunctions.SEND_BOOKING_CONFIRMATION), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          appointmentId,
          customerEmail: email,
          customerName: `${customerInfo?.firstName} ${customerInfo?.lastName}`,
          appointmentDate: format(appointmentDateTime, 'EEEE, MMMM d, yyyy'),
          appointmentTime: format(appointmentDateTime, 'h:mm a'),
          services: servicesText,
          staffName: selectedStaff?.name || null,
          totalPrice: formatPrice(totalPrice),
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Email sending failed:', errorData);
      }
    } catch (error) {
      console.error('Error calling email function:', error);
      // Don't throw - email failure shouldn't block the booking
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-serif text-[#232347] mb-2">Review Your Booking</h2>
      <p className="text-gray-600 mb-6">Please review your appointment details before confirming</p>
      
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
          <h3 className="font-medium mb-1">Error</h3>
          <p>{error}</p>
        </div>
      )}
      
      <div className="bg-[#F1F7F4] p-6 rounded-md mb-6">
        <h3 className="text-xl font-serif text-[#232347] mb-4">Appointment Summary</h3>
        
        {/* Date & Time */}
        <div className="mb-4">
          <h4 className="font-medium text-[#232347] mb-2">Date & Time</h4>
          <div className="flex items-center space-x-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B2D8C5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-gray-700">
              {format(appointmentDateTime, 'EEEE, MMMM d, yyyy')} at {format(appointmentDateTime, 'h:mm a')} - {format(appointmentEndTime, 'h:mm a')}
            </span>
          </div>
        </div>
        
        {/* Staff */}
        <div className="mb-4">
          <h4 className="font-medium text-[#232347] mb-2">Therapist</h4>
          <div className="flex items-center space-x-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B2D8C5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-gray-700">
              {selectedStaff ? selectedStaff.name : 'No preference (best available therapist)'}
            </span>
          </div>
        </div>
        
        {/* Services */}
        <div className="mb-4">
          <h4 className="font-medium text-[#232347] mb-2">Services</h4>
          <div className="space-y-2">
            {selectedServices.map(service => (
              <div key={service.id} className="flex justify-between items-center text-gray-700 pl-7">
                <span>{service.name} ({getNumericDuration(service)} min)</span>
                <span>${formatPrice(getNumericPrice(service))}</span>
              </div>
            ))}

            {/* Subtotal */}
            <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between text-gray-700 pl-7">
              <span>Subtotal</span>
              <span>${formatPrice(pricing.subtotal)}</span>
            </div>

            {/* Member Discount (if applicable) */}
            {pricing.memberDiscountAmount > 0 && (
              <div className="flex justify-between text-green-600 pl-7">
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Member Discount ({pricing.memberDiscountPercent}%)
                </span>
                <span>-${formatPrice(pricing.memberDiscountAmount)}</span>
              </div>
            )}

            {/* Total */}
            <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between font-medium text-[#232347] pl-7">
              <span>Total</span>
              <span>${formatPrice(pricing.afterDiscount)}</span>
            </div>

            {/* Wallet Balance Info (if user has wallet funds) */}
            {walletBalance > 0 && (
              <div className="mt-3 p-3 bg-blue-50 rounded-md pl-7">
                <div className="flex justify-between text-blue-700">
                  <span className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Wallet Balance
                  </span>
                  <span>${formatPrice(walletBalance)}</span>
                </div>
                {pricing.canPayWithWallet && (
                  <p className="text-sm text-blue-600 mt-1">
                    You can pay with your wallet balance on the next step.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Customer Info */}
        <div>
          <h4 className="font-medium text-[#232347] mb-2">Your Information</h4>
          <div className="space-y-2 pl-7">
            <div className="text-gray-700">
              {customerInfo.firstName} {customerInfo.lastName}
            </div>
            <div className="text-gray-700">
              Email: {customerInfo.email}
            </div>
            <div className="text-gray-700">
              Phone: {customerInfo.phone}
            </div>
            {customerInfo.notes && (
              <div className="text-gray-700">
                <p className="font-medium">Notes:</p>
                <p>{customerInfo.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Cancellation Policy */}
      <div className="mb-6 bg-white border border-gray-200 p-4 rounded-md">
        <h3 className="font-medium text-[#232347] mb-2">Cancellation Policy</h3>
        <p className="text-gray-600 text-sm">
          Please provide at least 24 hours notice for cancellations or rescheduling to avoid a cancellation fee of 50% of the service price. 
          No-shows will be charged the full service price.
        </p>
      </div>
      
      {/* Navigation buttons */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={prevStep}
          disabled={isSubmitting}
          className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        <button
          onClick={handleSubmitBooking}
          disabled={isSubmitting}
          className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {isSubmitting ? (
            <>
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
              Processing...
            </>
          ) : (
            'Confirm Booking'
          )}
        </button>
      </div>
    </div>
  );
};

export default BookingSummary;